import React, { useEffect, useState } from "react";
import {
  FaVideo,
  FaCalendarAlt,
  FaClock,
  FaSearch,
  FaBookOpen,
  FaChevronLeft,
  FaChevronRight,
  FaChalkboardTeacher,
  FaRegDotCircle,
  FaExclamationCircle,
  FaArrowRight
} from "react-icons/fa";
import axiosInstance from "../../../../axios";
import { Loading } from "../../../Other/Loading";

const StudentClasses = () => {
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusTab, setStatusTab] = useState("all"); // 'all' | 'live' | 'upcoming' | 'completed'
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const fetchStudentClasses = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/schedule/student");
      if (response.data && response.data.success) {
        setClasses(response.data.data || []);
        setFilteredClasses(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching student live classes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudentClasses();
  }, []);

  // Helper to determine status
  const getClassStatus = (startStr, endStr) => {
    const now = new Date();
    const start = new Date(startStr);
    const end = new Date(endStr);
    const fiveMinsBeforeStart = new Date(start.getTime() - 5 * 60 * 1000);

    if (now >= start && now <= end) {
      return "live";
    } else if (now >= fiveMinsBeforeStart && now < start) {
      return "upcoming_open";
    } else if (now < fiveMinsBeforeStart) {
      return "upcoming_locked";
    } else {
      return "completed";
    }
  };

  // Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  // Helper to format time
  const formatTime = (timeStr) => {
    if (!timeStr) return "N/A";
    const d = new Date(timeStr);
    return isNaN(d) ? timeStr : d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Filtering Logic
  useEffect(() => {
    let filtered = [...classes];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.class_title?.toLowerCase().includes(term) ||
          item.class_description?.toLowerCase().includes(term) ||
          item.courseName?.toLowerCase().includes(term) ||
          item.instructorName?.toLowerCase().includes(term)
      );
    }

    // Status Tab filter
    if (statusTab !== "all") {
      filtered = filtered.filter((item) => {
        const status = getClassStatus(item.start_time, item.end_time);
        if (statusTab === "upcoming") {
          return status === "upcoming_open" || status === "upcoming_locked";
        }
        return status === statusTab;
      });
    }

    setFilteredClasses(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusTab, classes]);

  // Pagination calculation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClasses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredClasses.length / itemsPerPage);

  // Statistics
  const liveCount = classes.filter((item) => getClassStatus(item.start_time, item.end_time) === "live").length;
  const upcomingCount = classes.filter((item) => ["upcoming_open", "upcoming_locked"].includes(getClassStatus(item.start_time, item.end_time))).length;
  const completedCount = classes.filter((item) => getClassStatus(item.start_time, item.end_time) === "completed").length;

  return (
    <div className="p-6 bg-slate-50/50 min-h-screen">
      {/* Header Panel */}
      <div className="mb-8 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-slate-500/10 rounded-full blur-2xl pointer-events-none" />
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative z-10">
          <div>
            <div className="flex items-center gap-3.5 mb-2.5">
              <div className="w-12 h-12 bg-white/10 border border-white/15 rounded-2xl flex items-center justify-center shadow-inner">
                <FaVideo className="text-indigo-400 text-2xl animate-pulse" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Live Class Schedules</h1>
                <p className="text-slate-400 text-xs sm:text-sm font-medium mt-0.5">Attend live sessions, interact with instructors, and learn in real time.</p>
              </div>
            </div>
          </div>

          {/* Stats Badges */}
          <div className="flex gap-3 flex-wrap">
            {[
              { label: "Live Now", value: liveCount, color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" },
              { label: "Upcoming", value: upcomingCount, color: "bg-indigo-500/10 border-indigo-500/30 text-indigo-400" },
              { label: "Completed", value: completedCount, color: "bg-slate-500/10 border-slate-500/30 text-slate-400" }
            ].map((stat, i) => (
              <div key={i} className={`px-5 py-3 rounded-2xl border ${stat.color} flex flex-col items-center justify-center min-w-[100px] backdrop-blur-xs`}>
                <span className="text-[10px] font-bold uppercase tracking-wider opacity-85">{stat.label}</span>
                <span className="text-xl sm:text-2xl font-black mt-0.5">{stat.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Toolbar - Search and Tab Filters */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-stretch lg:items-center mb-6">
        {/* Tab Filters */}
        <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm overflow-x-auto scrollbar-none">
          {[
            { id: "all", label: "All Classes" },
            { id: "live", label: "Live Now", count: liveCount, badge: "bg-emerald-500 text-white" },
            { id: "upcoming", label: "Upcoming", count: upcomingCount, badge: "bg-indigo-500 text-white" },
            { id: "completed", label: "Completed", count: completedCount, badge: "bg-slate-500 text-white" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setStatusTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                statusTab === tab.id
                  ? "bg-slate-900 text-white shadow-md"
                  : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-black ${
                  statusTab === tab.id ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative w-full lg:max-w-md">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
          <input
            type="text"
            placeholder="Search by topic, description, course or teacher..."
            className="pl-11 pr-4 py-3 w-full bg-white border border-slate-200 rounded-2xl shadow-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all text-slate-800 text-sm outline-none placeholder:text-slate-400 font-medium"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Main Grid View */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-slate-200 shadow-sm">
          <Loading />
        </div>
      ) : filteredClasses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-slate-200 shadow-sm text-center px-6">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 text-3xl mb-4">
            <FaExclamationCircle className="text-slate-300" />
          </div>
          <h3 className="text-lg font-black text-slate-800">No Live Classes Found</h3>
          <p className="text-slate-400 max-w-sm mt-1.5 text-sm font-medium">
            There are no classes scheduled matching your criteria or for courses you own.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentItems.map((item) => {
              const status = getClassStatus(item.start_time, item.end_time);
              
              // Status Badge Configs
              const statusConfig = {
                live: {
                  label: "Live Now",
                  classes: "bg-emerald-50 text-emerald-700 border-emerald-200",
                  dot: "bg-emerald-500 animate-ping",
                  btnText: "Join Google Meet",
                  btnClass: "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20 active:scale-95"
                },
                upcoming_open: {
                  label: "Upcoming (Open)",
                  classes: "bg-indigo-50 text-indigo-700 border-indigo-200",
                  dot: "bg-indigo-500 animate-pulse",
                  btnText: "Join Google Meet",
                  btnClass: "bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-600/20 active:scale-95"
                },
                upcoming_locked: {
                  label: "Upcoming (Locked)",
                  classes: "bg-slate-100 text-slate-500 border-slate-200",
                  dot: "bg-slate-300",
                  btnText: "Available 5m before",
                  btnClass: "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
                },
                completed: {
                  label: "Completed",
                  classes: "bg-slate-100 text-slate-600 border-slate-200",
                  dot: "bg-slate-400",
                  btnText: "Class Ended",
                  btnClass: "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
                }
              }[status];

              return (
                <div
                  key={item.id}
                  className="bg-white rounded-3xl shadow-xs hover:shadow-xl transition-all duration-300 border border-slate-200/80 overflow-hidden flex flex-col justify-between group"
                >
                  <div className="p-6">
                    {/* Header Row */}
                    <div className="flex justify-between items-center mb-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border capitalize tracking-wide bg-white shadow-2xs">
                        <span className={`w-2 h-2 rounded-full ${statusConfig.dot}`} />
                        {statusConfig.label}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50 px-2.5 py-1 rounded-lg">
                        ID: #{item.id}
                      </span>
                    </div>

                    {/* Course details */}
                    <div className="flex items-start gap-3.5 mb-4">
                      <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-center shrink-0">
                        <FaBookOpen className="text-slate-400 text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-extrabold text-slate-400 uppercase tracking-wider truncate" title={item.courseName}>
                          {item.courseName}
                        </p>
                        <h3 className="font-black text-slate-800 text-base leading-snug line-clamp-2 mt-0.5 group-hover:text-indigo-600 transition-colors">
                          {item.class_title}
                        </h3>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-slate-500 font-medium leading-relaxed mb-5 line-clamp-3 bg-slate-50/50 p-3 rounded-2xl border border-slate-100">
                      {item.class_description || "No description provided for this session."}
                    </p>

                    <div className="border-t border-slate-100 my-4" />

                    {/* Meta info */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                        <FaCalendarAlt className="text-slate-400 shrink-0" />
                        <span>{formatDate(item.start_time)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                        <FaClock className="text-slate-400 shrink-0" />
                        <span>
                          {formatTime(item.start_time)} – {formatTime(item.end_time)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
                        <FaChalkboardTeacher className="text-slate-400 shrink-0" />
                        <span>Instructor: <strong className="text-slate-700 font-extrabold">{item.instructorName}</strong></span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="p-6 pt-0">
                    {status === "live" || status === "upcoming_open" ? (
                      <a
                        href={item.google_meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-full py-3.5 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${statusConfig.btnClass}`}
                      >
                        <FaVideo className="text-sm" />
                        <span>{statusConfig.btnText}</span>
                        <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                      </a>
                    ) : (
                      <button
                        disabled
                        className={`w-full py-3.5 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 ${statusConfig.btnClass}`}
                      >
                        <span>{statusConfig.btnText}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-8 flex flex-col sm:flex-row justify-between items-center bg-white px-6 py-4 rounded-3xl border border-slate-200 shadow-sm gap-4">
              <span className="text-xs sm:text-sm text-slate-500 font-semibold">
                Showing{" "}
                <span className="font-extrabold text-slate-800">
                  {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, filteredClasses.length)}
                </span>{" "}
                of <span className="font-extrabold text-slate-800">{filteredClasses.length}</span> live classes
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-slate-200"
                >
                  <FaChevronLeft size={10} />
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-10 h-10 text-xs sm:text-sm font-bold rounded-xl transition-all cursor-pointer ${
                      currentPage === i + 1
                        ? "bg-slate-900 text-white shadow-md"
                        : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-slate-200"
                >
                  <FaChevronRight size={10} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StudentClasses;
